// SPDX-License-Identifier: LicenseRef-Proprietary
// Copyright (c) 2026 Invariant Holdings LLC
import { WASM_THEOREM_CODES, WASM_THEOREM_META } from "./meta.js";
const TRUSTED_AUTHORITIES = [
    "trusted-kernel",
    "trusted-db-readonly",
    "trusted-db-admin",
];
const BOUNDARY_POLICY = {
    "trusted-kernel": {
        scopes: ["database", "audit", "metrics"],
        operations: ["read", "write", "execute"],
    },
    "trusted-db-readonly": {
        scopes: ["database"],
        operations: ["read"],
    },
    "trusted-db-admin": {
        scopes: ["database"],
        operations: ["read", "write", "admin"],
    },
};
const ORIGIN_DELIMITER = "⌘";
const REACH_DELIMITER = "⌬";
export function createCompileTimeContractFragments(allocId) {
    const boundaries = new Map();
    const capabilities = new Map();
    const effects = new Map();
    return {
        permit_create_boundary(authority_source) {
            const source = authority_source.trim();
            if (source.length === 0) {
                throw new Error("PERMIT: authority source cannot be empty");
            }
            const policy = BOUNDARY_POLICY[source];
            if (!policy) {
                throw new Error(`PERMIT: '${source}' is not a trusted authority (allowed: ${TRUSTED_AUTHORITIES.join(", ")})`);
            }
            const id = allocId();
            boundaries.set(id, { authority_source: source, revoked: false });
            return id;
        },
        permit_grant(boundary_id, scope, operation) {
            const boundary = boundaries.get(boundary_id);
            if (!boundary)
                throw new Error(`PERMIT: boundary ${String(boundary_id)} not found`);
            if (boundary.revoked) {
                throw new Error(`PERMIT: boundary ${String(boundary_id)} has been revoked`);
            }
            const policy = BOUNDARY_POLICY[boundary.authority_source];
            if (!policy)
                throw new Error(`PERMIT: boundary ${String(boundary_id)} has no policy`);
            if (!policy.scopes.includes(scope)) {
                throw new Error(`PERMIT: boundary ${String(boundary_id)} not allowed to grant scope '${scope}'`);
            }
            if (!policy.operations.includes(operation)) {
                throw new Error(`PERMIT: boundary ${String(boundary_id)} not allowed to grant operation '${operation}'`);
            }
            const id = allocId();
            capabilities.set(id, { boundary_id, scope, operation, revoked: false });
            return id;
        },
        permit_validate(capability_id) {
            const cap = capabilities.get(capability_id);
            if (!cap)
                throw new Error(`PERMIT: capability ${String(capability_id)} not found`);
            if (cap.revoked) {
                throw new Error(`PERMIT: capability ${String(capability_id)} has been revoked`);
            }
            const boundary = boundaries.get(cap.boundary_id);
            if (!boundary || boundary.revoked) {
                throw new Error(`PERMIT: boundary for capability ${String(capability_id)} has been revoked`);
            }
            return `${cap.scope}:${cap.operation}@${boundary.authority_source}`;
        },
        permit_revoke(capability_id) {
            const cap = capabilities.get(capability_id);
            if (!cap)
                return false;
            cap.revoked = true;
            return true;
        },
        permit_revoke_boundary(boundary_id) {
            const boundary = boundaries.get(boundary_id);
            if (!boundary)
                return false;
            boundary.revoked = true;
            return true;
        },
        separate_as_data(value) {
            return `DATA:${value}`;
        },
        separate_as_instruction(value) {
            return `INSTR:${value}`;
        },
        separate_safe_query(template, param) {
            const instrTemplate = template.startsWith("INSTR:")
                ? template
                : `INSTR:${template}`;
            const dataParam = param.startsWith("DATA:") ? param : `DATA:${param}`;
            return JSON.stringify({ template: instrTemplate, params: [dataParam] });
        },
        origin_brand(value, label) {
            if (label.length === 0) {
                throw new Error("DATA.ORIGIN: label must not be empty");
            }
            if (value.includes(ORIGIN_DELIMITER) ||
                label.includes(ORIGIN_DELIMITER)) {
                throw new Error("DATA.ORIGIN: value or label contains reserved delimiter");
            }
            if (value.includes(REACH_DELIMITER) || label.includes(REACH_DELIMITER)) {
                throw new Error("DATA.ORIGIN: value or label contains cross-namespace delimiter");
            }
            return `${label}${ORIGIN_DELIMITER}${value}`;
        },
        origin_verify(branded) {
            const idx = branded.indexOf(ORIGIN_DELIMITER);
            if (idx < 0)
                throw new Error("DATA.ORIGIN: value is not branded");
            return branded.slice(idx + 1);
        },
        origin_check_source(branded, expected_source) {
            const idx = branded.indexOf(ORIGIN_DELIMITER);
            if (idx < 0)
                return false;
            return branded.slice(0, idx) === expected_source;
        },
        reach_classify(value, level) {
            if (!Number.isInteger(level) || level < 0) {
                throw new Error("DATA.REACH: level must be a non-negative integer");
            }
            if (value.includes(REACH_DELIMITER)) {
                throw new Error("DATA.REACH: value contains reserved delimiter");
            }
            return `${String(level)}${REACH_DELIMITER}${value}`;
        },
        reach_declassify(branded, target_level) {
            if (!Number.isInteger(target_level) || target_level < 0) {
                throw new Error("DATA.REACH: target_level must be a non-negative integer");
            }
            const idx = branded.indexOf(REACH_DELIMITER);
            if (idx < 0)
                throw new Error("DATA.REACH: value is not classified");
            const currentLevel = parseInt(branded.slice(0, idx), 10);
            if (Number.isNaN(currentLevel)) {
                throw new Error("DATA.REACH: malformed classification level");
            }
            if (target_level > currentLevel) {
                throw new Error(`DATA.REACH: upward flow denied (${String(currentLevel)} → ${String(target_level)})`);
            }
            const inner = branded.slice(idx + 1);
            return `${String(target_level)}${REACH_DELIMITER}${inner}`;
        },
        reach_level(branded) {
            const idx = branded.indexOf(REACH_DELIMITER);
            if (idx < 0)
                throw new Error("DATA.REACH: value is not classified");
            const level = parseInt(branded.slice(0, idx), 10);
            if (Number.isNaN(level)) {
                throw new Error("DATA.REACH: malformed classification level");
            }
            return level;
        },
        precommit_create(description) {
            const id = allocId();
            effects.set(id, { description, state: "uncommitted" });
            return id;
        },
        precommit_commit(handle_id) {
            const record = effects.get(handle_id);
            if (!record)
                throw new Error(`PRECOMMIT: effect ${String(handle_id)} not found`);
            if (record.state !== "uncommitted") {
                throw new Error(`PRECOMMIT: effect ${String(handle_id)} is ${record.state}, cannot commit`);
            }
            record.state = "committed";
        },
        precommit_cancel(handle_id) {
            const record = effects.get(handle_id);
            if (!record)
                throw new Error(`PRECOMMIT: effect ${String(handle_id)} not found`);
            if (record.state !== "uncommitted") {
                throw new Error(`PRECOMMIT: effect ${String(handle_id)} is ${record.state}, cannot cancel`);
            }
            record.state = "cancelled";
        },
        precommit_state(handle_id) {
            const record = effects.get(handle_id);
            if (!record)
                throw new Error(`PRECOMMIT: effect ${String(handle_id)} not found`);
            return record.state;
        },
        precommit_description(handle_id) {
            const record = effects.get(handle_id);
            if (!record)
                throw new Error(`PRECOMMIT: effect ${String(handle_id)} not found`);
            return record.description;
        },
    };
}
export function createMetadataContract(version) {
    return {
        csk_version: () => version,
        csk_available_theorems: () => WASM_THEOREM_CODES.map((code) => {
            const meta = WASM_THEOREM_META[code];
            if (meta === undefined) {
                throw new Error(`WASM theorem metadata is missing ${String(code)}`);
            }
            return `${meta.name} (${meta.code}) — ${meta.structure}`;
        }).join("\n"),
    };
}
//# sourceMappingURL=compile-time-contracts.js.map